// ============================================================================
// CHANGO V5.0 — Customer shared form
// ============================================================================
// Used by /app/customers/new and the editable customer detail header. Field
// names intentionally match the /api/customers POST/PATCH contract.
// ============================================================================

import React, { useEffect, useState } from 'react';

const BLANK = {
  customer_code: '',
  company_name: '',
  name_en: '',
  address: '',
  address_en: '',
  country_code: '',
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  incoterms: '',
  payment_terms: '',
  rating: '',
  group_id: '',
};

function normalizeCustomer(customer) {
  if (!customer) return BLANK;
  return {
    customer_code: customer.customer_code || '',
    company_name: customer.company_name || '',
    name_en: customer.name_en || '',
    address: customer.address || '',
    address_en: customer.address_en || '',
    country_code: customer.country_code || '',
    primary_contact_name: customer.primary_contact_name || '',
    primary_contact_email: customer.primary_contact_email || '',
    primary_contact_phone: customer.primary_contact_phone || '',
    incoterms: customer.incoterms || '',
    payment_terms: customer.payment_terms || '',
    rating: customer.rating == null ? '' : String(customer.rating),
    group_id: customer.group_id == null ? '' : String(customer.group_id),
  };
}

function optionalText(value) {
  const s = String(value || '').trim();
  return s || null;
}

function payloadFromForm(form) {
  return {
    customer_code: optionalText(form.customer_code),
    company_name: form.company_name.trim(),
    name_en: optionalText(form.name_en),
    address: optionalText(form.address),
    address_en: optionalText(form.address_en),
    country_code: optionalText(form.country_code),
    primary_contact_name: optionalText(form.primary_contact_name),
    primary_contact_email: optionalText(form.primary_contact_email),
    primary_contact_phone: optionalText(form.primary_contact_phone),
    incoterms: optionalText(form.incoterms),
    payment_terms: optionalText(form.payment_terms),
    rating: form.rating === '' ? null : Number(form.rating),
    group_id: form.group_id === '' ? null : Number(form.group_id),
  };
}

export default function CustomerForm({
  initialCustomer = null,
  groups = [],
  saving = false,
  submitLabel = '保存',
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = useState(() => normalizeCustomer(initialCustomer));
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm(normalizeCustomer(initialCustomer));
    setError(null);
  }, [initialCustomer]);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!form.company_name.trim()) {
      setError('客户名称必填');
      return;
    }
    if (form.rating !== '') {
      const rating = Number(form.rating);
      if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
        setError('评级必须是 1-5');
        return;
      }
    }

    try {
      await onSubmit(payloadFromForm(form));
    } catch (err) {
      setError(err?.message || '保存失败');
    }
  };

  const textField = (key, label, props = {}) => (
    <label className="block text-sm text-slate-600">
      {label}
      <input
        type="text"
        className="field mt-1"
        value={form[key] || ''}
        onChange={(event) => setField(key, event.target.value)}
        disabled={saving}
        {...props}
      />
    </label>
  );

  return (
    <form onSubmit={submit} className="card p-5 space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        {textField('customer_code', '客户编号', { maxLength: 50, placeholder: 'C-202605-000001' })}
        {textField('company_name', '客户名称 *', { maxLength: 200, autoFocus: !initialCustomer })}
        {textField('name_en', '英文名称', { maxLength: 200 })}
        {textField('country_code', '国家/地区代码', { maxLength: 100, placeholder: 'CN, US, JP...' })}
        {textField('address', '中文地址', { maxLength: 500 })}
        {textField('address_en', '英文地址', { maxLength: 500 })}
        {textField('primary_contact_name', '主联系人', { maxLength: 120 })}
        {textField('primary_contact_email', '邮箱', { type: 'email', maxLength: 200 })}
        {textField('primary_contact_phone', '电话', { maxLength: 60 })}
        {textField('incoterms', '贸易条款', { maxLength: 100, placeholder: 'FOB, CIF...' })}
        {textField('payment_terms', '付款条款', { maxLength: 100 })}

        <label className="block text-sm text-slate-600">
          评级
          <select
            className="field mt-1"
            value={form.rating}
            onChange={(event) => setField('rating', event.target.value)}
            disabled={saving}
          >
            <option value="">未评级</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </label>

        <label className="block text-sm text-slate-600 sm:col-span-2">
          客户分组
          <select
            className="field mt-1"
            value={form.group_id}
            onChange={(event) => setField('group_id', event.target.value)}
            disabled={saving}
          >
            <option value="">不分组</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p className="err text-sm">{error}</p>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button type="button" className="btn-ghost text-sm" onClick={onCancel} disabled={saving}>
            取消
          </button>
        )}
        <button type="submit" className="btn-primary text-sm" disabled={saving}>
          {saving ? '保存中...' : submitLabel}
        </button>
      </div>
    </form>
  );
}
